// blitzpaypagecomponents/BalanceLedgerModal.jsx - FULL SCREEN OVERLAY DESIGN
import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  Button,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Divider,
  Chip,
  Stack,
  Alert,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  InputAdornment,
  Avatar,
  Tabs,
  Tab,
  useMediaQuery,
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Fade,
  Slide,
} from "@mui/material";
import {
  Close as CloseIcon,
  AccountBalanceWallet as WalletIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  AttachMoney as WithdrawIcon,
  SwapHoriz as TransferIcon,
  Receipt as ReceiptIcon,
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Paid as PaidIcon,
  AddCircle as AddCircleIcon,
  RemoveCircle as RemoveCircleIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Payment as PaymentIcon,
  Campaign as CampaignIcon,
  Link as LinkIcon,
  Percent as PercentIcon,
} from "@mui/icons-material";
import { useTheme } from '@mui/material/styles';
import { useMutation, useQueryClient, useQuery } from "react-query";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { formatCurrency } from "../../../Utils/formatNumbers";
import client from "../../../API";
import useAuth from "../../../Hooks/use-auth";
import { useCreatorAuth } from "../../../Hooks/creator-use-auth";
import AddBalanceDialog from "../../../Components/tools/addBalance";

const BalanceLedgerModal = ({
  open,
  onClose,
  currentBalance: propBalance,
  transitBalance: propTransitBalance,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  
  // Dual Auth Support
  const { getCurrrentUser, isAuthorized: isUserAuthorized } = useAuth();
  const { creatorToken, isInitialized: isCreatorInitialized, isAuthorized: isCreatorAuthorized } = useCreatorAuth();
  
  const queryClient = useQueryClient();

  // Determine current user based on auth type
  const currentUser = useMemo(() => {
    if (isCreatorAuthorized && creatorToken?.creator_user) {
      return creatorToken.creator_user;
    }
    if (isUserAuthorized) {
      return getCurrrentUser();
    }
    return null;
  }, [isCreatorAuthorized, isUserAuthorized, creatorToken, getCurrrentUser]);

  // Determine if we're in creator context
  const isCreatorContext = isCreatorAuthorized && Boolean(creatorToken?.creator_user);

  // Wait for creator auth to initialize if in creator context
  const isInitialized = isCreatorContext ? isCreatorInitialized : true;

  // AppShell spacing constant
  const APPSHELL_HEIGHT = 86;

  // Add Balance Dialog State
  const [showAddBalanceDialog, setShowAddBalanceDialog] = useState(false);

  // Fetch balance for both creators and business users
  const {
    data: internalBalanceData,
    isLoading: internalBalanceLoading,
    error: internalBalanceError,
    refetch: refetchInternalBalance
  } = useQuery(
    ["myBalance", isCreatorContext],
    () => client.balanceLedger.fetchMyBalance(),
    {
      enabled: !!currentUser && isInitialized && open && (isCreatorContext ? !!currentUser?.username : !!currentUser?.id),
      retry: (failureCount, error) => {
        if (error?.response?.status === 403) return false;
        return failureCount < 2;
      },
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 3000),
      refetchOnWindowFocus: false,
      staleTime: 30000,
      onSuccess: (data) => {
        console.log('[BalanceLedger] Internal balance data fetched:', data);
      },
      onError: (error) => {
        console.error('[BalanceLedger] Internal balance fetch failed:', error);
      },
    }
  );

  // Use the proper user ID based on context
  const effectiveUserId = useMemo(() => {
    if (isCreatorContext) {
      return internalBalanceData?.user?.id;
    } else {
      return internalBalanceData?.user?.id || currentUser?.id || currentUser?.user_id;
    }
  }, [isCreatorContext, internalBalanceData, currentUser]);

  console.log('[BalanceLedger] Context:', { 
    isCreatorContext, 
    currentUser, 
    effectiveUserId,
    internalBalanceData: internalBalanceData?.user
  });

  // Fetch wallets based on user type
  const { 
    data: walletsData, 
    isLoading: walletsLoading,
    error: walletsError,
    refetch: refetchWallets 
  } = useQuery(
    ["userWallets", isCreatorContext],
    () => {
      console.log('[BalanceLedger] Fetching wallets, isCreatorContext:', isCreatorContext);
      if (isCreatorContext) {
        return client.creatorConnect.getWallets();
      } else {
        return client.userWallets.getWallets();
      }
    },
    {
      enabled: !!currentUser && isInitialized && (isCreatorContext ? !!currentUser?.username : !!currentUser?.id),
      retry: 2,
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 3000),
      refetchOnWindowFocus: false,
      staleTime: 60000,
      onSuccess: (data) => {
        console.log('[BalanceLedger] Wallets fetched successfully:', data);
      },
      onError: (error) => {
        console.error('[BalanceLedger] Wallets fetch failed:', error);
        console.error('[BalanceLedger] Error response:', error?.response?.data);
      },
    }
  );

  const wallets = walletsData?.wallets || [];
  const primaryWalletId = walletsData?.primary_wallet_id;

  // Fetch ledger entries using the effective user ID
  const { 
    data: ledgerEntriesResponse, 
    isLoading: ledgerLoading, 
    isError, 
    error,
    refetch: refetchLedger 
  } = useQuery(
    ["balanceLedgerHistory", effectiveUserId, isCreatorContext],
    () => {
      console.log('[BalanceLedger] Fetching ledger history for user ID:', effectiveUserId);
      return client.balanceLedger.fetch(effectiveUserId);
    },
    {
      enabled: !!effectiveUserId && open && isInitialized,
      retry: (failureCount, error) => {
        if (error?.response?.status === 403) return false;
        return failureCount < 2;
      },
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 3000),
      refetchOnWindowFocus: false,
      staleTime: 30000,
      select: (data) => {
        console.log('[BalanceLedger] Raw ledger data:', data);
        if (Array.isArray(data)) return data;
        if (data?.entries && Array.isArray(data.entries)) return data.entries;
        if (data?.ledger && Array.isArray(data.ledger)) return data.ledger;
        if (data?.data && Array.isArray(data.data)) return data.data;
        if (data?.transactions && Array.isArray(data.transactions)) return data.transactions;
        return [];
      },
      onSuccess: (data) => {
        console.log('[BalanceLedger] Ledger history fetched successfully, entries:', data?.length);
      },
      onError: (error) => {
        console.error('[BalanceLedger] Ledger fetch failed:', error);
        console.error('[BalanceLedger] Error response:', error?.response?.data);
        if (error?.response?.status !== 403) {
          toast.error("Failed to fetch balance history", {
            description: "Please try refreshing"
          });
        }
      },
    }
  );

  // Fetch detailed withdrawal history for rich display
  const {
    data: withdrawalHistoryData,
    isLoading: withdrawalsLoading,
    refetch: refetchWithdrawalHistory
  } = useQuery(
    ["withdrawalHistory", isCreatorContext, currentUser?.id || currentUser?.username],
    () => client.withdrawals.userGetHistory({ limit: 100 }),
    {
      enabled: !!currentUser && isInitialized && open,
      staleTime: 30000,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        console.log('[BalanceLedger] Withdrawal history fetched:', data);
      },
      onError: (error) => {
        console.error('[BalanceLedger] Withdrawal history fetch failed:', error);
      },
    }
  );

  // Filter to only show balance withdrawals
  const balanceWithdrawals = useMemo(() => {
    if (!withdrawalHistoryData?.withdrawals) return [];
    return withdrawalHistoryData.withdrawals.filter((w) => {
      const brief = (w.brief || '').toUpperCase();
      if (brief.includes('PAYOUT')) {
        return false;
      }
      return true;
    });
  }, [withdrawalHistoryData]);

  // Map withdrawal IDs to their rich details for quick lookup
  const withdrawalDetailsMap = useMemo(() => {
    if (!withdrawalHistoryData?.withdrawals) return {};
    return withdrawalHistoryData.withdrawals.reduce((acc, w) => {
      acc[w.id] = w;
      if (w.reference) {
        acc[w.reference] = w;
      }
      return acc;
    }, {});
  }, [withdrawalHistoryData]);

  // Force refetch when dialog opens
  useEffect(() => {
    if (open && currentUser && isInitialized) {
      console.log('[BalanceLedger] Dialog opened, refetching data...', { isCreatorContext, currentUser });
      refetchInternalBalance();
      refetchWallets();
      refetchWithdrawalHistory();
      if (effectiveUserId) {
        refetchLedger();
      }
      queryClient.invalidateQueries(["balanceLedger"]);
      queryClient.invalidateQueries(["myBalance"]);
      queryClient.invalidateQueries(["withdrawalHistory"]);
    }
  }, [open, currentUser, isInitialized, isCreatorContext, effectiveUserId]);

  const ledgerEntries = ledgerEntriesResponse || [];

  // Use internal balance data for both creators and business users, with fallback to props
  const currentBalance = useMemo(() => {
    if (internalBalanceData) {
      return parseFloat(internalBalanceData.balance || 0);
    }
    return parseFloat(propBalance || 0);
  }, [internalBalanceData, propBalance]);

  const transitBalance = useMemo(() => {
    if (internalBalanceData) {
      return parseFloat(internalBalanceData.transit_balance || 0);
    }
    return parseFloat(propTransitBalance || 0);
  }, [internalBalanceData, propTransitBalance]);

  const isLoading = ledgerLoading || !isInitialized || internalBalanceLoading || withdrawalsLoading;

  // Withdrawal state
  const [showWithdrawDialog, setShowWithdrawDialog] = useState(false);
  const [showWithdrawErrorDialog, setShowWithdrawErrorDialog] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [selectedWalletId, setSelectedWalletId] = useState("");
  const [filterTab, setFilterTab] = useState(0);

  // Get selected wallet details
  const selectedWallet = useMemo(() => {
    return wallets.find(w => w.id === selectedWalletId);
  }, [wallets, selectedWalletId]);

  // Auto-select primary wallet when dialog opens
  useEffect(() => {
    if (showWithdrawDialog && primaryWalletId && !selectedWalletId) {
      setSelectedWalletId(primaryWalletId);
    }
  }, [showWithdrawDialog, primaryWalletId, selectedWalletId]);

  // Refetch balance when withdrawal dialog opens
  useEffect(() => {
    if (showWithdrawDialog && isCreatorContext) {
      console.log('[BalanceLedger] Withdrawal dialog opened, refetching balance...');
      refetchInternalBalance();
    }
  }, [showWithdrawDialog, isCreatorContext, refetchInternalBalance]);

  // Withdrawal mutation
  const withdrawalMutation = useMutation(
    (withdrawalData) => client.withdrawals.userInstantRequest(withdrawalData),
    {
      onSuccess: (data) => {
        toast.success("Withdrawal processing started!", {
          description: `$${parseFloat(withdrawAmount).toFixed(2)} is being sent to ${selectedWallet?.nickname || 'your wallet'}`,
        });

        setWithdrawAmount("");
        setSelectedWalletId("");
        setShowWithdrawDialog(false);

        queryClient.invalidateQueries(["balanceLedger"]);
        queryClient.invalidateQueries(["balanceLedgerHistory"]);
        queryClient.invalidateQueries(["myBalance"]);
        queryClient.invalidateQueries(["withdrawals"]);
        queryClient.invalidateQueries(["withdrawalHistory"]);
        if (isCreatorContext) {
          refetchInternalBalance();
        }
        refetchLedger();
      },
      onError: (error) => {
        setShowWithdrawErrorDialog(true);
      },
    }
  );

  // Check if wallet type supports instant withdrawal
  const supportsInstantWithdrawal = (walletType) => {
    return ['paypal', 'stripe', 'bank_ach', 'international_bank'].includes(walletType);
  };

  const validateWithdrawal = () => {
    const amount = parseFloat(withdrawAmount);
    if (!withdrawAmount || isNaN(amount) || amount <= 0) {
      toast.error("Please enter a valid amount");
      return false;
    }

    if (!selectedWalletId || !selectedWallet) {
      toast.error("Please select a payment method");
      return false;
    }

    if (!supportsInstantWithdrawal(selectedWallet?.wallet_type)) {
      toast.error("Selected payment method doesn't support instant withdrawal", {
        description: "Please select PayPal or Stripe for instant processing"
      });
      return false;
    }

    if (amount > currentBalance) {
      toast.error("Insufficient balance", {
        description: `Available: $${currentBalance.toFixed(2)}`
      });
      return false;
    }

    if (amount < 10) {
      toast.error("Minimum withdrawal amount is $10.00");
      return false;
    }

    return true;
  };

  const handleWithdrawNow = () => {
    if (!validateWithdrawal()) {
      return;
    }

    const walletType = selectedWallet?.wallet_type;
    let paymentService = 'paypal';
    let recipientDetails = {};

    switch (walletType) {
      case 'paypal':
        paymentService = 'paypal';
        recipientDetails = { paypal_email: selectedWallet?.paypal_email };
        break;
      case 'stripe':
        paymentService = 'stripe';
        recipientDetails = { stripe_account_id: selectedWallet?.stripe_account_id };
        break;
      case 'international_bank':
        paymentService = 'international_bank';
        recipientDetails = { wallet_id: selectedWallet?.id };
        break;
      case 'bank_ach':
        paymentService = 'bank_ach';
        recipientDetails = {
          bank_account_number: selectedWallet?.bank_account_number,
          bank_routing_number: selectedWallet?.bank_routing_number,
          wallet_id: selectedWallet?.id,
        };
        break;
      case 'bank_transfer':
      case 'crypto':
        toast.error("Instant withdrawal not available for this payment method");
        return;
      default:
        paymentService = 'paypal';
        recipientDetails = { paypal_email: getWalletDestination(selectedWallet) };
    }

    const withdrawalData = {
      amount: parseFloat(withdrawAmount),
      payment_service: paymentService,
      recipient_details: recipientDetails,
      notes: `Withdrawal to ${selectedWallet?.nickname || selectedWallet?.wallet_type}`,
    };

    withdrawalMutation.mutate(withdrawalData);
  };

  const handleTransferToUser = () => {
    onClose();
    navigate(isCreatorContext 
      ? "/creatorconnect/internal-transfer" 
      : "/blitzpay/internal-transfer"
    );
  };

  const getWalletDestination = (wallet) => {
    if (!wallet) return "";
    
    switch (wallet.wallet_type) {
      case "paypal":
        return wallet.paypal_email || "";
      case "wise":
        return wallet.wise_email || "";
      case "bank_transfer":
        return wallet.bank_account_number || wallet.iban || "";
      case "crypto":
        return wallet.crypto_wallet_address || "";
      default:
        return "";
    }
  };

  const getWalletIcon = (walletType) => {
    switch (walletType) {
      case "paypal":
        return <PaymentIcon />;
      case "wise":
        return <AccountBalanceIcon />;
      case "bank_transfer":
        return <AccountBalanceIcon />;
      case "crypto":
        return <WalletIcon />;
      default:
        return <WalletIcon />;
    }
  };

  const getWalletDisplayDetails = (wallet) => {
    switch (wallet.wallet_type) {
      case "paypal":
        return wallet.paypal_email;
      case "wise":
        return `${wallet.wise_email} (${wallet.wise_currency})`;
      case "bank_transfer":
        return `${wallet.bank_name || 'Bank'} ${wallet.bank_account_number || ''}`;
      case "crypto":
        return `${wallet.crypto_currency} • ${wallet.crypto_wallet_address?.slice(0, 6)}...${wallet.crypto_wallet_address?.slice(-4)}`;
      default:
        return wallet.nickname || 'Payment Method';
    }
  };

  const calculateWithdrawalFee = (amount, walletType) => {
    const amt = parseFloat(amount) || 0;
    switch (walletType) {
      case "paypal":
        return amt * 0.02;
      case "wise":
        return amt * 0.015;
      case "bank_transfer":
        return 0;
      case "crypto":
        return amt * 0.01;
      default:
        return 0;
    }
  };

  const withdrawalFee = calculateWithdrawalFee(withdrawAmount, selectedWallet?.wallet_type);
  const withdrawalTotal = parseFloat(withdrawAmount || 0) + withdrawalFee;

  // Transaction display helpers
  const getTransactionDisplay = (entry) => {
    const operationType = entry.operation_type?.toLowerCase() || "";
    const changeAmount = parseFloat(entry.change_amount || entry.amount || 0);
    const isCredit = changeAmount > 0;
    const details = entry.details || {};
    const metadata = details.comprehensive_metadata || details;
    
    if (operationType.includes('commission')) {
      const commissionType = details.commission_type || metadata.commission_type || 'general';
      const sourceType = details.source_type || metadata.source_type || '';
      const sourceId = details.source_id || metadata.source_id || '';
      
      if (sourceType === 'campaign' || commissionType === 'project') {
        const campaignName = metadata.custom?.campaign_name || 
                            metadata.campaign_name ||
                            details.campaign_name ||
                            `Campaign #${sourceId}`;
        const commissionRate = details.commission_percentage || 
                              (details.commission_rate ? `${(parseFloat(details.commission_rate) * 100).toFixed(2)}%` : '');
        
        return {
          category: 'commission',
          icon: <CampaignIcon />,
          color: 'success',
          bgColor: 'success.lighter',
          title: `Campaign Commission`,
          subtitle: `${campaignName}${commissionRate ? ` • ${commissionRate}` : ''}`,
          isCredit: true,
        };
      }
      
      if (sourceType === 'invoice' && details.referral_id) {
        const referralId = details.referral_id;
        const endUserName = metadata.end_user?.name || 
                           details.end_user_name || 
                           'referral';
        const invoiceAmount = details.invoice_amount || details.source_amount || '';
        
        return {
          category: 'commission',
          icon: <LinkIcon />,
          color: 'success',
          bgColor: 'success.lighter',
          title: `Referral Commission #${referralId}`,
          subtitle: `From ${endUserName}${invoiceAmount ? ` • Invoice: ${formatCurrency(parseFloat(invoiceAmount))}` : ''}`,
          isCredit: true,
        };
      }
      
      if (commissionType === 'blitzpay') {
        const invoiceId = details.invoice_id || sourceId;
        const commissionRate = details.commission_percentage || '';
        
        return {
          category: 'commission',
          icon: <PercentIcon />,
          color: 'success',
          bgColor: 'success.lighter',
          title: `BlitzPay Commission`,
          subtitle: `Invoice #${invoiceId}${commissionRate ? ` • ${commissionRate}` : ''}`,
          isCredit: true,
        };
      }
      
      const referralId = details.referral_id || 'N/A';
      return {
        category: 'commission',
        icon: <PaidIcon />,
        color: 'success',
        bgColor: 'success.lighter',
        title: `Commission Earned`,
        subtitle: sourceType ? `${sourceType}${sourceId ? ` #${sourceId}` : ''}` : `Referral #${referralId}`,
        isCredit: true,
      };
    }
    
    if (operationType.includes('internal_transfer') || entry.brief?.toLowerCase().includes('transfer')) {
      return {
        category: 'transfer',
        icon: isCredit ? <TrendingUpIcon /> : <TrendingDownIcon />,
        color: isCredit ? 'success' : 'primary',
        bgColor: isCredit ? 'success.lighter' : 'primary.lighter',
        title: isCredit ? 'Transfer Received' : 'Transfer Sent',
        subtitle: entry.details?.description || entry.reason || (isCredit ? 'Money received' : 'Money sent'),
        isCredit,
      };
    }
    
    if (operationType.includes('payment') || operationType.includes('deposit')) {
      const provider = details.provider || 'payment';
      
      return {
        category: 'payment',
        icon: <AddCircleIcon />,
        color: isCredit ? 'success' : 'error',
        bgColor: isCredit ? 'success.lighter' : 'error.lighter',
        title: isCredit ? 'Balance Funding' : 'Payment',
        subtitle: `${formatCurrency(Math.abs(changeAmount))}${isCredit ? ' added to balance' : ' paid'}`,
        isCredit,
      };
    }
    
    if (operationType.includes('withdrawal') || entry.brief?.toLowerCase().includes('withdraw')) {
      const withdrawalId = details.withdrawal_id || entry.external_transaction_id;
      const withdrawalRef = details.reference;
      const withdrawalDetails = withdrawalDetailsMap[withdrawalId] ||
                                withdrawalDetailsMap[withdrawalRef] ||
                                null;

      const status = withdrawalDetails?.status || entry.status || 'completed';
      const paymentService = withdrawalDetails?.payment_service ||
                             withdrawalDetails?.manual_service ||
                             details.service ||
                             'wallet';

      const getStatusColor = () => {
        switch (status) {
          case 'paid': return 'success';
          case 'processing': return 'info';
          case 'pending':
          case 'approved': return 'warning';
          case 'failed':
          case 'cancelled':
          case 'reversed': return 'error';
          default: return 'warning';
        }
      };

      const getStatusLabel = () => {
        switch (status) {
          case 'paid': return 'Completed';
          case 'processing': return 'Processing';
          case 'pending': return 'Pending';
          case 'approved': return 'Approved';
          case 'failed': return 'Failed';
          case 'cancelled': return 'Cancelled';
          case 'reversed': return 'Reversed';
          default: return status.charAt(0).toUpperCase() + status.slice(1);
        }
      };

      const paymentMethodLabel = paymentService.charAt(0).toUpperCase() + paymentService.slice(1);

      return {
        category: 'withdrawal',
        icon: <WithdrawIcon />,
        color: getStatusColor(),
        bgColor: `${getStatusColor()}.lighter`,
        title: 'Withdrawal',
        subtitle: `${paymentMethodLabel} • ${getStatusLabel()}`,
        isCredit: false,
        withdrawalDetails,
      };
    }
    
    return {
      category: 'other',
      icon: isCredit ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />,
      color: isCredit ? 'success' : 'text.primary',
      bgColor: isCredit ? 'success.lighter' : 'grey.100',
      title: entry.brief || entry.operation_type?.replace(/_/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || (isCredit ? 'Credit' : 'Debit'),
      subtitle: entry.details?.description || entry.reason || '',
      isCredit,
    };
  };

  const getTransactionCategory = (entry) => {
    const display = getTransactionDisplay(entry);
    return display.category;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    });
  };

  const filteredEntries = React.useMemo(() => {
    if (!Array.isArray(ledgerEntries)) {
      return [];
    }
    
    if (filterTab === 0) return ledgerEntries;
    
    return ledgerEntries.filter((entry) => {
      const category = getTransactionCategory(entry);
      if (filterTab === 1) return category === "transfer";
      if (filterTab === 2) return category === "payment";
      if (filterTab === 3) return category === "withdrawal";
      if (filterTab === 4) return category === "commission";
      return true;
    });
  }, [ledgerEntries, filterTab]);

  const getCategoryCount = (category) => {
    if (category === 'withdrawal') {
      return balanceWithdrawals?.length || 0;
    }
    if (!Array.isArray(ledgerEntries)) return 0;
    return ledgerEntries.filter((entry) => getTransactionCategory(entry) === category).length;
  };

  // ─── Withdrawal Error Dialog (shared between mobile & desktop) ───────────────
  const WithdrawErrorDialog = (
    <Dialog
      open={showWithdrawErrorDialog}
      onClose={() => setShowWithdrawErrorDialog(false)}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      sx={{ zIndex: theme.zIndex.modal + 3 }}
    >
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            bgcolor: 'error.lighter',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto',
            mb: 2.5,
          }}
        >
          <WithdrawIcon sx={{ fontSize: 36, color: 'error.main' }} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
          Withdrawal didn't go through
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
          Hi — for whatever reason, withdrawing failed. We are actively focused on fixing this as soon as possible!
        </Typography>
        <Button
          variant="contained"
          fullWidth
          onClick={() => setShowWithdrawErrorDialog(false)}
          sx={{ borderRadius: 2, py: 1.5, fontWeight: 700, textTransform: 'none' }}
        >
          Got it
        </Button>
      </Box>
    </Dialog>
  );

  // Show initialization loading
  if (!isInitialized) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 3,
            m: 2,
            ...(isMobile && {
              borderRadius: '24px 24px 0 0',
              maxHeight: `calc(100vh - ${APPSHELL_HEIGHT}px)`,
              m: 0,
              width: '100%',
              maxWidth: '100%',
              position: 'fixed',
              bottom: `${APPSHELL_HEIGHT}px`,
              left: 0,
              right: 0,
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
            }),
          }
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, px: 3 }}>
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Initializing...
          </Typography>
        </Box>
      </Dialog>
    );
  }

  // Show error for 403
  if (isError && error?.response?.status === 403) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 3,
            m: 2,
          }
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Blitz Balance
            </Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <Alert severity="warning">
            <Typography variant="body2">
              You don't have permission to view balance information. This feature may not be available for your account type.
            </Typography>
          </Alert>
          <Button onClick={onClose} variant="contained" fullWidth sx={{ mt: 3 }}>
            Close
          </Button>
        </Box>
      </Dialog>
    );
  }

  // Mobile View - Keep as dialog
  if (isMobile) {
    return (
      <>
        <Dialog
          open={open}
          onClose={onClose}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { 
              borderRadius: '24px 24px 0 0',
              maxHeight: `calc(100vh - ${APPSHELL_HEIGHT}px)`,
              m: 0,
              width: '100%',
              maxWidth: '100%',
              position: 'fixed',
              bottom: `${APPSHELL_HEIGHT}px`,
              left: 0,
              right: 0,
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
            }
          }}
          sx={{
            zIndex: theme.zIndex.modal + 1,
            '& .MuiDialog-container': {
              alignItems: 'flex-end',
            },
          }}
        >
          <Box sx={{ pb: 1, pt: 3, px: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <WalletIcon sx={{ color: 'white', fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                    Blitz Balance
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    All transactions & transfers
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton 
                  onClick={() => {
                    queryClient.invalidateQueries(["balanceLedger"]);
                    queryClient.invalidateQueries(["myBalance"]);
                    if (isCreatorContext) {
                      refetchInternalBalance();
                    }
                    refetchWallets();
                    refetchLedger();
                  }}
                  size="small"
                >
                  <RefreshIcon />
                </IconButton>
                <IconButton onClick={onClose} size="small">
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>
          </Box>

          <Box sx={{ px: 3, pb: 3, overflowY: 'auto' }}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 3,
                borderRadius: 3,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Typography variant="caption" sx={{ opacity: 0.9, fontWeight: 500, letterSpacing: '0.5px' }}>
                  SPENDABLE BALANCE
                </Typography>
                <Typography 
                  variant="h3" 
                  sx={{ 
                    fontWeight: 700,
                    mt: 0.5,
                    mb: 1,
                    fontSize: '2rem',
                  }}
                >
                  {formatCurrency(currentBalance || 0)}
                </Typography>

                {transitBalance > 0 && (
                  <Box sx={{ mb: 2, opacity: 0.9 }}>
                    <Typography variant="caption" sx={{ fontWeight: 500 }}>
                      + {formatCurrency(transitBalance)} in transit
                    </Typography>
                  </Box>
                )}

                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    <Button
                      variant="contained"
                      startIcon={<AddCircleIcon />}
                      onClick={() => setShowAddBalanceDialog(true)}
                      fullWidth
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.25)',
                        color: 'white',
                        '&:hover': {
                          bgcolor: 'rgba(255,255,255,0.35)',
                        },
                      }}
                    >
                      Add
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      variant="contained"
                      startIcon={<WithdrawIcon />}
                      onClick={() => setShowWithdrawDialog(true)}
                      fullWidth
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.25)',
                        color: 'white',
                        '&:hover': {
                          bgcolor: 'rgba(255,255,255,0.35)',
                        },
                      }}
                    >
                      Withdraw
                    </Button>
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      startIcon={<TransferIcon />}
                      onClick={handleTransferToUser}
                      fullWidth
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.25)',
                        color: 'white',
                        '&:hover': {
                          bgcolor: 'rgba(255,255,255,0.35)',
                        },
                      }}
                    >
                      Transfer to User
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Paper>

            {isLoading && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
                <CircularProgress size={40} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Loading...
                </Typography>
              </Box>
            )}

            {!isLoading && !isError && Array.isArray(ledgerEntries) && ledgerEntries.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Tabs 
                  value={filterTab} 
                  onChange={(e, newValue) => setFilterTab(newValue)}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  <Tab label={`All (${ledgerEntries.length})`} />
                  <Tab label={`Transfers (${getCategoryCount('transfer')})`} />
                  <Tab label={`Payments (${getCategoryCount('payment')})`} />
                  <Tab label={`Withdrawals (${getCategoryCount('withdrawal')})`} />
                  <Tab label={`💰 Commissions (${getCategoryCount('commission')})`} />
                </Tabs>
              </Box>
            )}

            {!isLoading && !isError && filteredEntries.length > 0 && filterTab !== 3 && (
              <Stack spacing={1.5}>
                {filteredEntries.slice(0, 20).map((entry) => {
                  const display = getTransactionDisplay(entry);
                  const amount = Math.abs(parseFloat(entry.change_amount || entry.amount || 0));
                  
                  return (
                    <Paper
                      key={entry.id}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar 
                          sx={{ 
                            bgcolor: display.bgColor,
                            color: `${display.color}.main`,
                            width: 40,
                            height: 40,
                          }}
                        >
                          {display.icon}
                        </Avatar>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                            {display.title}
                          </Typography>
                          {display.subtitle && (
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                              {display.subtitle}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(entry.created_at || entry.timestamp)}
                          </Typography>
                        </Box>

                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 700,
                            color: display.isCredit ? 'success.main' : 'text.primary',
                          }}
                        >
                          {display.isCredit ? '+' : '-'}${amount.toFixed(2)}
                        </Typography>
                      </Box>
                    </Paper>
                  );
                })}
              </Stack>
            )}

            {!isLoading && !isError && filterTab === 3 && (
              <Stack spacing={1.5}>
                {balanceWithdrawals.slice(0, 20).map((withdrawal) => {
                  const status = withdrawal.status || 'pending';
                  const amount = parseFloat(withdrawal.amount_no_fee || withdrawal.amount || 0);

                  return (
                    <Paper
                      key={withdrawal.id}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                          sx={{
                            bgcolor: 'warning.lighter',
                            color: 'warning.main',
                            width: 40,
                            height: 40,
                          }}
                        >
                          <WithdrawIcon />
                        </Avatar>

                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Withdrawal
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(withdrawal.created_at)}
                          </Typography>
                        </Box>

                        <Typography variant="body1" sx={{ fontWeight: 700 }}>
                          -${amount.toFixed(2)}
                        </Typography>
                      </Box>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </Box>

          <Divider />

          <Box sx={{ p: 3 }}>
            <Button onClick={onClose} variant="contained" fullWidth>
              Close
            </Button>
          </Box>
        </Dialog>

        {/* Withdrawal Dialog for Mobile */}
        <Dialog
          open={showWithdrawDialog}
          onClose={() => !withdrawalMutation.isLoading && setShowWithdrawDialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { 
              borderRadius: '24px 24px 0 0',
              maxHeight: `calc(100vh - ${APPSHELL_HEIGHT}px)`,
              m: 0,
              width: '100%',
              maxWidth: '100%',
              position: 'fixed',
              bottom: `${APPSHELL_HEIGHT}px`,
              left: 0,
              right: 0,
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
            }
          }}
          sx={{
            zIndex: theme.zIndex.modal + 2,
            '& .MuiDialog-container': {
              alignItems: 'flex-end',
            },
          }}
        >
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <WithdrawIcon sx={{ color: 'white' }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Withdraw Balance
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Instant processing
                  </Typography>
                </Box>
              </Box>
              <IconButton
                onClick={() => setShowWithdrawDialog(false)}
                disabled={withdrawalMutation.isLoading}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <TextField
              label="Amount"
              type="number"
              value={withdrawAmount}
              onChange={(e) => setWithdrawAmount(e.target.value)}
              fullWidth
              required
              inputProps={{ min: 10, step: 0.01 }}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              sx={{ mb: 3 }}
              helperText={`Available: $${currentBalance.toFixed(2)}`}
            />

            {walletsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
                <FormLabel component="legend" sx={{ mb: 2 }}>
                  Payment Method
                </FormLabel>
                <RadioGroup
                  value={selectedWalletId}
                  onChange={(e) => setSelectedWalletId(parseInt(e.target.value, 10))}
                >
                  {wallets.map((wallet) => {
                    const isInstantSupported = supportsInstantWithdrawal(wallet.wallet_type);
                    return (
                      <Paper
                        key={wallet.id}
                        sx={{
                          p: 2,
                          mb: 1.5,
                          border: '2px solid',
                          borderColor: selectedWalletId === wallet.id ? 'primary.main' : 'divider',
                          borderRadius: 2,
                          opacity: isInstantSupported ? 1 : 0.5,
                        }}
                        onClick={() => isInstantSupported && setSelectedWalletId(wallet.id)}
                      >
                        <FormControlLabel
                          value={wallet.id}
                          control={<Radio disabled={!isInstantSupported} />}
                          label={
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {wallet.nickname || wallet.wallet_type}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {getWalletDisplayDetails(wallet)}
                              </Typography>
                            </Box>
                          }
                        />
                      </Paper>
                    );
                  })}
                </RadioGroup>
              </FormControl>
            )}

            <Stack direction="row" spacing={2}>
              <Button
                onClick={() => setShowWithdrawDialog(false)}
                disabled={withdrawalMutation.isLoading}
                fullWidth
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleWithdrawNow}
                disabled={!withdrawAmount || !selectedWalletId || withdrawalMutation.isLoading}
                fullWidth
              >
                {withdrawalMutation.isLoading ? 'Processing...' : 'Withdraw'}
              </Button>
            </Stack>
          </Box>
        </Dialog>

        {/* Withdrawal Error Dialog - Mobile */}
        {WithdrawErrorDialog}

        <AddBalanceDialog 
          open={showAddBalanceDialog}
          onClose={() => {
            setShowAddBalanceDialog(false);
            queryClient.invalidateQueries(["balanceLedger"]);
            queryClient.invalidateQueries(["myBalance"]);
            if (isCreatorContext) {
              refetchInternalBalance();
            }
            refetchLedger();
          }}
        />
      </>
    );
  }

  // Desktop View - Full Screen Overlay
  return (
    <>
      <Fade in={open} timeout={300}>
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'background.default',
            zIndex: theme.zIndex.modal + 1,
            display: open ? 'flex' : 'none',
            flexDirection: 'column',
          }}
        >
          {/* Header Bar */}
          <Box
            sx={{
              height: 80,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 4,
              bgcolor: 'background.paper',
              backdropFilter: 'blur(20px)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2.5,
                  background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <WalletIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  Your Blitz Balance
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Complete transaction history and balance management
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => {
                  queryClient.invalidateQueries(["balanceLedger"]);
                  queryClient.invalidateQueries(["myBalance"]);
                  if (isCreatorContext) {
                    refetchInternalBalance();
                  }
                  refetchWallets();
                  refetchLedger();
                }}
                sx={{ borderRadius: 2 }}
              >
                Refresh
              </Button>
              <IconButton 
                onClick={onClose}
                sx={{
                  bgcolor: 'action.hover',
                  '&:hover': { bgcolor: 'action.selected' },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Main Content - Two Column Layout */}
          <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
            {/* Left Column - Balance Info */}
            <Box
              sx={{
                width: 420,
                borderRight: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.paper',
              }}
            >
              <Box sx={{ p: 4, flex: 1 }}>
                {/* Balance Card */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    mb: 4,
                    borderRadius: 4,
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: '100%',
                      height: '100%',
                      background: 'radial-gradient(circle at top right, rgba(255,255,255,0.2) 0%, transparent 70%)',
                      pointerEvents: 'none',
                    },
                  }}
                >
                  <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Typography variant="caption" sx={{ opacity: 0.9, fontWeight: 600, letterSpacing: '1px' }}>
                      SPENDABLE BALANCE
                    </Typography>
                    <Typography 
                      variant="h2" 
                      sx={{ 
                        fontWeight: 800,
                        mt: 1,
                        mb: 2,
                        fontSize: '3.5rem',
                      }}
                    >
                      {formatCurrency(currentBalance || 0)}
                    </Typography>

                    {transitBalance > 0 && (
                      <Box sx={{ mb: 3, opacity: 0.95 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          + {formatCurrency(transitBalance)} in transit
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.85 }}>
                          Pending conversion to spendable balance
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Paper>

                {/* Quick Stats */}
                <Grid container spacing={2} sx={{ mb: 4 }}>
                  <Grid item xs={6}>
                    <Paper
                      sx={{
                        p: 2.5,
                        borderRadius: 2.5,
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                        <Avatar
                          sx={{
                            bgcolor: 'success.lighter',
                            color: 'success.main',
                            width: 36,
                            height: 36,
                          }}
                        >
                          <TrendingUpIcon fontSize="small" />
                        </Avatar>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          CREDITS
                        </Typography>
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {ledgerEntries.filter(e => parseFloat(e.change_amount || 0) > 0).length}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6}>
                    <Paper
                      sx={{
                        p: 2.5,
                        borderRadius: 2.5,
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                        <Avatar
                          sx={{
                            bgcolor: 'error.lighter',
                            color: 'error.main',
                            width: 36,
                            height: 36,
                          }}
                        >
                          <TrendingDownIcon fontSize="small" />
                        </Avatar>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          DEBITS
                        </Typography>
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {ledgerEntries.filter(e => parseFloat(e.change_amount || 0) < 0).length}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {/* Action Buttons */}
                <Stack spacing={2}>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<AddCircleIcon />}
                    onClick={() => setShowAddBalanceDialog(true)}
                    fullWidth
                    sx={{
                      py: 2,
                      borderRadius: 2.5,
                      fontWeight: 700,
                      fontSize: '1rem',
                      textTransform: 'none',
                      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      '&:hover': {
                        background: "linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)",
                      },
                    }}
                  >
                    Add Balance
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<WithdrawIcon />}
                    onClick={() => setShowWithdrawDialog(true)}
                    fullWidth
                    sx={{
                      py: 2,
                      borderRadius: 2.5,
                      fontWeight: 700,
                      fontSize: '1rem',
                      textTransform: 'none',
                      borderWidth: 2,
                      '&:hover': {
                        borderWidth: 2,
                      },
                    }}
                  >
                    Withdraw Funds
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<TransferIcon />}
                    onClick={handleTransferToUser}
                    fullWidth
                    sx={{
                      py: 2,
                      borderRadius: 2.5,
                      fontWeight: 700,
                      fontSize: '1rem',
                      textTransform: 'none',
                      borderWidth: 2,
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      '&:hover': {
                        borderWidth: 2,
                        bgcolor: 'primary.lighter',
                      },
                    }}
                  >
                    Transfer to User
                  </Button>
                </Stack>
              </Box>
            </Box>

            {/* Right Column - Transaction History */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {isLoading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
                  <CircularProgress size={48} />
                  <Typography variant="body1" color="text.secondary" sx={{ mt: 3 }}>
                    Loading transaction history...
                  </Typography>
                </Box>
              ) : (
                <>
                  {/* Filter Tabs */}
                  {!isError && Array.isArray(ledgerEntries) && ledgerEntries.length > 0 && (
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 4, pt: 2 }}>
                      <Tabs 
                        value={filterTab} 
                        onChange={(e, newValue) => setFilterTab(newValue)}
                        sx={{
                          '& .MuiTab-root': {
                            minHeight: 56,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.95rem',
                            px: 3,
                          },
                        }}
                      >
                        <Tab label={`All (${ledgerEntries.length})`} />
                        <Tab label={`Transfers (${getCategoryCount('transfer')})`} />
                        <Tab label={`Payments (${getCategoryCount('payment')})`} />
                        <Tab label={`Withdrawals (${getCategoryCount('withdrawal')})`} />
                        <Tab 
                          label={`💰 Commissions (${getCategoryCount('commission')})`}
                          sx={{ color: 'success.main' }}
                        />
                      </Tabs>
                    </Box>
                  )}

                  {/* Transaction List */}
                  <Box sx={{ flex: 1, overflowY: 'auto', p: 4 }}>
                    {!isError && filteredEntries.length > 0 && filterTab !== 3 && (
                      <Stack spacing={2}>
                        {filteredEntries.map((entry) => {
                          const display = getTransactionDisplay(entry);
                          const amount = Math.abs(parseFloat(entry.change_amount || entry.amount || 0));
                          
                          return (
                            <Paper
                              key={entry.id}
                              sx={{
                                p: 3,
                                borderRadius: 2.5,
                                border: '1px solid',
                                borderColor: 'divider',
                                transition: 'all 0.2s',
                                '&:hover': {
                                  borderColor: `${display.color}.main`,
                                  boxShadow: 4,
                                  transform: 'translateY(-2px)',
                                },
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                                <Avatar 
                                  sx={{ 
                                    bgcolor: display.bgColor,
                                    color: `${display.color}.main`,
                                    width: 56,
                                    height: 56,
                                  }}
                                >
                                  {display.icon}
                                </Avatar>

                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                                    {display.title}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                    {display.subtitle}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {formatDate(entry.created_at || entry.timestamp)}
                                  </Typography>
                                </Box>

                                <Box sx={{ textAlign: 'right' }}>
                                  <Typography
                                    variant="h5"
                                    sx={{
                                      fontWeight: 800,
                                      color: display.isCredit ? 'success.main' : 'text.primary',
                                      mb: 0.5,
                                    }}
                                  >
                                    {display.isCredit ? '+' : '-'}${amount.toFixed(2)}
                                  </Typography>
                                  {entry.status && (
                                    <Chip
                                      label={entry.status}
                                      size="small"
                                      color={entry.status === "completed" || entry.status === "complete" ? "success" : "default"}
                                      sx={{ 
                                        height: 24, 
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        textTransform: 'capitalize',
                                      }}
                                    />
                                  )}
                                </Box>
                              </Box>

                              <Box 
                                sx={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  alignItems: 'center',
                                  pt: 2,
                                  mt: 2,
                                  borderTop: '1px solid',
                                  borderColor: 'divider',
                                }}
                              >
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                                  Balance After
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                  {formatCurrency(entry.new_balance || entry.balance_after || 0)}
                                </Typography>
                              </Box>
                            </Paper>
                          );
                        })}
                      </Stack>
                    )}

                    {/* Withdrawal History Tab */}
                    {!isError && filterTab === 3 && (
                      <Box>
                        {balanceWithdrawals?.length > 0 ? (
                          <Stack spacing={2}>
                            {balanceWithdrawals.map((withdrawal) => {
                              const status = withdrawal.status || 'pending';
                              const paymentService = withdrawal.payment_service || withdrawal.manual_service || 'wallet';
                              const amount = parseFloat(withdrawal.amount_no_fee || withdrawal.amount || 0);

                              const getStatusColor = () => {
                                switch (status) {
                                  case 'paid': return 'success';
                                  case 'processing': return 'info';
                                  case 'pending':
                                  case 'approved': return 'warning';
                                  case 'failed':
                                  case 'cancelled':
                                  case 'reversed': return 'error';
                                  default: return 'warning';
                                }
                              };

                              const getStatusLabel = () => {
                                switch (status) {
                                  case 'paid': return 'Completed';
                                  case 'processing': return 'Processing';
                                  case 'pending': return 'Pending';
                                  case 'approved': return 'Approved';
                                  case 'failed': return 'Failed';
                                  case 'cancelled': return 'Cancelled';
                                  case 'reversed': return 'Reversed';
                                  default: return status.charAt(0).toUpperCase() + status.slice(1);
                                }
                              };

                              const paymentMethodLabel = paymentService.charAt(0).toUpperCase() + paymentService.slice(1);

                              return (
                                <Paper
                                  key={withdrawal.id}
                                  sx={{
                                    p: 3,
                                    borderRadius: 2.5,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                      borderColor: `${getStatusColor()}.main`,
                                      boxShadow: 4,
                                      transform: 'translateY(-2px)',
                                    },
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                                    <Avatar
                                      sx={{
                                        bgcolor: `${getStatusColor()}.lighter`,
                                        color: `${getStatusColor()}.main`,
                                        width: 56,
                                        height: 56,
                                      }}
                                    >
                                      <WithdrawIcon sx={{ fontSize: 28 }} />
                                    </Avatar>

                                    <Box sx={{ flex: 1 }}>
                                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                                        Withdrawal
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                        {paymentMethodLabel} • {getStatusLabel()}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {formatDate(withdrawal.created_at)}
                                      </Typography>
                                    </Box>

                                    <Box sx={{ textAlign: 'right' }}>
                                      <Typography
                                        variant="h5"
                                        sx={{
                                          fontWeight: 800,
                                          mb: 0.5,
                                        }}
                                      >
                                        -${amount.toFixed(2)}
                                      </Typography>
                                      <Chip
                                        label={getStatusLabel()}
                                        size="small"
                                        color={getStatusColor()}
                                        sx={{
                                          height: 24,
                                          fontSize: '0.75rem',
                                          fontWeight: 600,
                                        }}
                                      />
                                    </Box>
                                  </Box>

                                  {withdrawal.recipient_email && (
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        pt: 2,
                                        mt: 2,
                                        borderTop: '1px solid',
                                        borderColor: 'divider',
                                      }}
                                    >
                                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                                        Sent to
                                      </Typography>
                                      <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                        {withdrawal.recipient_email}
                                      </Typography>
                                    </Box>
                                  )}
                                </Paper>
                              );
                            })}
                          </Stack>
                        ) : (
                          <Box sx={{ textAlign: 'center', py: 12 }}>
                            <Box
                              sx={{
                                width: 120,
                                height: 120,
                                borderRadius: '50%',
                                backgroundColor: 'grey.100',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto',
                                mb: 3,
                              }}
                            >
                              <WithdrawIcon sx={{ fontSize: 60, color: 'grey.400' }} />
                            </Box>
                            <Typography variant="h5" color="text.primary" gutterBottom sx={{ fontWeight: 700 }}>
                              No Withdrawals Yet
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500, margin: '0 auto' }}>
                              Your withdrawal history will appear here once you make your first withdrawal.
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}

                    {/* Empty States */}
                    {!isError && filterTab !== 3 && Array.isArray(ledgerEntries) && ledgerEntries.length > 0 && filteredEntries.length === 0 && (
                      <Box sx={{ textAlign: 'center', py: 12 }}>
                        <Box
                          sx={{
                            width: 120,
                            height: 120,
                            borderRadius: '50%',
                            backgroundColor: 'grey.100',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto',
                            mb: 3,
                          }}
                        >
                          <TransferIcon sx={{ fontSize: 60, color: 'grey.400' }} />
                        </Box>
                        <Typography variant="h5" color="text.primary" gutterBottom sx={{ fontWeight: 700 }}>
                          No {filterTab === 1 ? 'Transfers' : filterTab === 2 ? 'Payments' : 'Commissions'} Yet
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500, margin: '0 auto' }}>
                          Transactions in this category will appear here.
                        </Typography>
                      </Box>
                    )}

                    {!isError && (!Array.isArray(ledgerEntries) || ledgerEntries.length === 0) && (
                      <Box sx={{ textAlign: 'center', py: 12 }}>
                        <Box
                          sx={{
                            width: 120,
                            height: 120,
                            borderRadius: '50%',
                            backgroundColor: 'grey.100',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto',
                            mb: 3,
                          }}
                        >
                          <WalletIcon sx={{ fontSize: 60, color: 'grey.400' }} />
                        </Box>
                        <Typography variant="h5" color="text.primary" gutterBottom sx={{ fontWeight: 700 }}>
                          No Transaction History
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500, margin: '0 auto', mb: 4 }}>
                          Your balance transaction history will appear here once you start making payments, transfers, and receiving funds.
                        </Typography>
                        <Button
                          variant="contained"
                          size="large"
                          startIcon={<AddCircleIcon />}
                          onClick={() => setShowAddBalanceDialog(true)}
                          sx={{ 
                            borderRadius: 2,
                            px: 4,
                            py: 1.5,
                          }}
                        >
                          Add Your First Balance
                        </Button>
                      </Box>
                    )}
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Fade>

      {/* Withdrawal Dialog - Desktop */}
      <Dialog
        open={showWithdrawDialog}
        onClose={() => !withdrawalMutation.isLoading && setShowWithdrawDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 3,
            m: 2,
          }
        }}
        sx={{
          zIndex: theme.zIndex.modal + 2,
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <WithdrawIcon sx={{ color: 'white' }} />
              </Box>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Withdraw Balance
                  </Typography>
                  <Chip
                    label="⚡ Instant"
                    size="small"
                    color="success"
                    sx={{ height: 22, fontSize: '0.75rem', fontWeight: 600 }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Processed immediately to your payment method
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={() => setShowWithdrawDialog(false)}
              disabled={withdrawalMutation.isLoading}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <TextField
            label="Withdrawal Amount"
            type="number"
            value={withdrawAmount}
            onChange={(e) => setWithdrawAmount(e.target.value)}
            fullWidth
            required
            variant="outlined"
            inputProps={{ min: 10, step: 0.01 }}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            sx={{ mb: 3 }}
            helperText={
              (isCreatorContext && internalBalanceLoading)
                ? "Loading balance..."
                : withdrawAmount && parseFloat(withdrawAmount) > currentBalance
                  ? `Insufficient balance (Available: $${currentBalance.toFixed(2)})`
                  : `Available: $${currentBalance.toFixed(2)} • Minimum: $10.00`
            }
            error={withdrawAmount && parseFloat(withdrawAmount) > currentBalance}
          />

          {walletsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : walletsError ? (
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="body2">
                Failed to load payment methods: {walletsError.message}
              </Typography>
            </Alert>
          ) : wallets.length === 0 ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                No payment methods found. Please add a payment method in your settings first.
              </Typography>
            </Alert>
          ) : (
            <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
              <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600 }}>
                Select Payment Method
              </FormLabel>
              <RadioGroup
                value={selectedWalletId}
                onChange={(e) => setSelectedWalletId(parseInt(e.target.value, 10))}
              >
                {wallets.map((wallet) => {
                  const isInstantSupported = supportsInstantWithdrawal(wallet.wallet_type);
                  return (
                    <Paper
                      key={wallet.id}
                      sx={{
                        p: 2,
                        mb: 1.5,
                        border: '2px solid',
                        borderColor: selectedWalletId === wallet.id ? 'primary.main' : 'divider',
                        borderRadius: 2,
                        cursor: isInstantSupported ? 'pointer' : 'not-allowed',
                        opacity: isInstantSupported ? 1 : 0.5,
                        transition: 'all 0.2s',
                        '&:hover': isInstantSupported ? {
                          borderColor: 'primary.main',
                          boxShadow: 2,
                        } : {},
                      }}
                      onClick={() => isInstantSupported && setSelectedWalletId(wallet.id)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <FormControlLabel
                          value={wallet.id}
                          control={<Radio disabled={!isInstantSupported} />}
                          label=""
                          sx={{ m: 0 }}
                        />

                        <Avatar
                          sx={{
                            bgcolor: selectedWalletId === wallet.id ? 'primary.lighter' : 'grey.100',
                            color: selectedWalletId === wallet.id ? 'primary.main' : 'text.secondary',
                          }}
                        >
                          {getWalletIcon(wallet.wallet_type)}
                        </Avatar>

                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              {wallet.nickname || wallet.wallet_type.replace('_', ' ').toUpperCase()}
                            </Typography>
                            {wallet.id === primaryWalletId && (
                              <Chip
                                label="Primary"
                                size="small"
                                color="primary"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                            {isInstantSupported && (
                              <Chip
                                label="⚡ Instant"
                                size="small"
                                color="success"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                            {!isInstantSupported && (
                              <Chip
                                label="Not Available"
                                size="small"
                                color="default"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {getWalletDisplayDetails(wallet)}
                          </Typography>
                          {!isInstantSupported && (
                            <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.5 }}>
                              Instant withdrawal not available for this method
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  );
                })}
              </RadioGroup>
            </FormControl>
          )}

          {withdrawAmount && selectedWallet && (
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default', mb: 3 }}>
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Withdrawal Amount:</Typography>
                  <Typography variant="body2" fontWeight={600}>${parseFloat(withdrawAmount).toFixed(2)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Fee ({selectedWallet.wallet_type === 'bank_transfer' ? 'Free' : `${(calculateWithdrawalFee(1, selectedWallet.wallet_type) * 100).toFixed(1)}%`}):
                  </Typography>
                  <Typography variant="body2" color="error.main">
                    {withdrawalFee === 0 ? 'Free' : `-$${withdrawalFee.toFixed(2)}`}
                  </Typography>
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body1" fontWeight={600}>You Receive:</Typography>
                  <Typography variant="body1" fontWeight={700} color="success.main">
                    ${(parseFloat(withdrawAmount) - withdrawalFee).toFixed(2)}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          )}

          <Stack direction="row" spacing={2}>
            <Button
              onClick={() => setShowWithdrawDialog(false)}
              disabled={withdrawalMutation.isLoading}
              fullWidth
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleWithdrawNow}
              disabled={!withdrawAmount || !selectedWalletId || withdrawalMutation.isLoading || wallets.length === 0 || !supportsInstantWithdrawal(selectedWallet?.wallet_type) || (isCreatorContext && internalBalanceLoading)}
              startIcon={withdrawalMutation.isLoading ? <CircularProgress size={16} color="inherit" /> : <span>⚡</span>}
              fullWidth
            >
              {withdrawalMutation.isLoading ? 'Processing...' : (isCreatorContext && internalBalanceLoading) ? 'Loading balance...' : 'Withdraw Now'}
            </Button>
          </Stack>
        </Box>
      </Dialog>

      {/* Withdrawal Error Dialog - Desktop */}
      {WithdrawErrorDialog}

      <AddBalanceDialog 
        open={showAddBalanceDialog}
        onClose={() => {
          setShowAddBalanceDialog(false);
          queryClient.invalidateQueries(["balanceLedger"]);
          queryClient.invalidateQueries(["balanceLedgerHistory"]);
          queryClient.invalidateQueries(["myBalance"]);
          if (isCreatorContext) {
            refetchInternalBalance();
          }
          refetchLedger();
        }}
      />
    </>
  );
};

export default BalanceLedgerModal;